import React from 'react';
import { SectionFolder } from '@src/components/SectionFolder';
import { SectionTitle } from '@src/components/SectionTitle';
import { SectionProps } from '../types/Settings';
import { ThemeProvider, Tooltip } from '@mui/material';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import { AutoComplete, RenderTag } from '@src/components/AutoComplete';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { CollectionView, WidgetView } from 'common';
import { AutoCompleteOption } from '@src/types/AutoComplete';
import { getSourceType } from '@src/pages/Widget/utils/sourceType';
import { ArrayHelper } from '@src/services/helpers/array';
import { style } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import { LabelPill } from '@src/components/LabelPill';
import { useQueryCollections } from '@src/queries/collections/useQueryCollections';
import { SECTION_TITLE_SIZE } from '../utils/variables';
import { SectionContent } from '../subcomponents/SectionContent';
import { materialTheme } from '../subcomponents/Theme';
import { createChangeSettings } from '../utils/createChangeSettings';
import { ProductCollectionFilterIcon } from '../../Icons/DropDownIcons/ProductCollectionFilter';

export const ProductCollectionFilter = ({
  opened,
  onOpen,
  editedWidget,
  setEditedWidget,
}: SectionProps) => {
  // Check the editedWidget instead of remoteWidget to reflect current edits
  const hasProductList = Array.isArray(editedWidget.settings.default_sources?.product_list);
  const isProductType = hasProductList;
  const changeSettings = createChangeSettings(editedWidget, setEditedWidget);

  // Only render this component if widget type is product
  if (!isProductType) return null;

  // get all collections
  const { data: allCollections, isLoading: isLoadingCollections } = useQueryCollections();
  const collectionsMap = ArrayHelper.mapToObject(allCollections || [], (c) => c._id);

  function renderCollectionTag(id: string, onRemove: Parameters<RenderTag>[1]) {
    let label: string = '';

    if (allCollections) {
      label = collectionsMap[id]?.name;
    }

    if (!label) {
      return (
        <Tooltip title={lang.tooltips.unauthorizedCollection} key={id}>
          <span>{renderTag(id, lang.fields.unauthorizedCollectionName, onRemove)}</span>
        </Tooltip>
      );
    }
    return renderTag(id, label, onRemove);
  }

  const renderTag = (key: string, label: string, onRemove: () => void) => (
    <LabelPill onRemove={onRemove} key={key}>
      {label}
    </LabelPill>
  );

  function handleAddCollection(id: string) {
    changeSettings({
      default_sources: {
        ...(editedWidget.settings.default_sources || {}),
        collection_list: [...(editedWidget.settings.default_sources?.collection_list || []), id],
      },
    });
  }

  function handleRemoveCollection(id: string) {
    const sourceCopy = [...(editedWidget.settings.default_sources?.collection_list || [])];
    ArrayHelper.removeOneFrom(sourceCopy, id);
    changeSettings({
      default_sources: {
        ...(editedWidget.settings.default_sources || {}),
        collection_list: sourceCopy,
      },
    });
  }

  function collectionsToOptions(collections: CollectionView[] = []): AutoCompleteOption[] {
    return collections.map((s) => ({ id: s._id, label: s.name }));
  }

  return (
    <SectionFolder
      title={
        <SectionTitle size={SECTION_TITLE_SIZE}>
          <ProductCollectionFilterIcon />
          {lang.productCollectionFilter}
        </SectionTitle>
      }
      opened={opened}
      afterTitle={
        <Tooltip title={lang.tooltips.collectionFilter} placement="top">
          <span className={infoProductCollectionFilterClass}>
            <InfoSvgIcon />
          </span>
        </Tooltip>
      }
      onOpen={onOpen}
    >
      <SectionContent>
        <ThemeProvider theme={materialTheme}>
          <AutoComplete
            id="collections-filter"
            title={lang.fields.collectionPlaceHolder}
            loading={isLoadingCollections}
            onAdd={handleAddCollection}
            onRemove={handleRemoveCollection}
            options={collectionsToOptions(allCollections)}
            selected={editedWidget.settings.default_sources?.collection_list}
            renderTag={renderCollectionTag}
          />
        </ThemeProvider>
      </SectionContent>
    </SectionFolder>
  );
};

const lang = LocalizedStrings({
  en: {
    productCollectionFilter: 'Collection Filter',
    tooltips: {
      collectionFilter:
        'Use this field if you want to display UGCs that are only present in the specified collection(s)',
      unauthorizedCollection:
        'To modify this collection, request access to the collection from the widget owner',
    },
    fields: {
      collectionPlaceHolder: 'choose a collection',
      unauthorizedCollectionName: 'Private collection',
    },
  },
});

const infoProductCollectionFilterClass = style({
  marginLeft: 10,
  color: ADALONG_COLORS.LIGHT_GRAY,
});

export default ProductCollectionFilter;
